<template>
    <CqModal
        :showModal="show"
        @closeModal="closeModal"
        :preventClosing="isSaving"
        heading="Ziekteperiode"
    >
        <div class="sick-period" v-for="sickPeriod in sickPeriods" :key="sickPeriod.id ?? 'NEWPERIOD'">
            <CustomDateSelect label="Ingangsdatum" v-model="sickPeriod.beginDate" :max="date" required />
            <CustomDateSelect label="Verwachte hersteldatum" v-model="sickPeriod.expectedRecoveryDate" :min="addDay(date)" />
            <CustomDateSelect label="Hersteldatum" v-model="sickPeriod.endDate" :min="addDay(date)" />

            <h3>Ziekteverloop in percentages</h3>
            <div class="percentageGrid">
                <template v-for="(percentage, index) in sickPeriod.percentages" :key="percentage.id ?? ('i' + index)">
                    <CustomDateSelect
                        label="Vanaf"
                        class="percentageDateInput"
                        v-model="percentage.beginDate"
                        :disabled="index === 0"
                        :min="getPercentageEarliestSelectableDate(sickPeriod, index)"
                        :max="getPercentageLatestSelectableDate(sickPeriod, index)"
                        hideTodayButton
                        required
                        noMargin
                    />
                    <CqTextInput type="number" v-model="percentage.percentage" :min="0.01" :max="100" :step="0.01" required noMargin />
                    <CqButton v-if="index > 0" dangerous icon="remove" @click="deletePercentage(sickPeriod, index)" margin="none" />
                </template>
            </div>
            <div class="percentageAddButton">
                <CqButton label="Toevoegen" class="no-shadow" @click="addPercentage(sickPeriod)" margin="none" />
            </div>
        </div>

        <CqButtonBar>
            <CqButton primary :disabled="isSaving" @click="saveSickPeriods" label="Sla ziekteperiode op" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {CqButton, CqButtonBar, CqModal, CqTextInput} from '@cquential/ui';
    import Notify from '@/components/modals/Notify.vue';
    import CustomDateSelect from '@/components/Branding/CustomDateSelect.vue';
    import {SickPeriod, ApiClient, ApiError, HttpStatusCode} from '@cquential/api';
    import Dates from '@cquential/date-utils';
    import {Component, Vue} from 'vue-facing-decorator';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            CqTextInput,
            CustomDateSelect,
        },
        emits: [
            'update:sickPeriod',
            'create:sickPeriod',
        ],
    })
    export default class SickPeriodEditModal extends Vue {
        private static instance: SickPeriodEditModal | null = null;

        private date: Date = new Date();
        private sickPeriods: SickPeriod[] = [];
        private isSaving: boolean = false;
        private show: boolean = false;

        public mounted(): void {
            SickPeriodEditModal.instance = this;
        }

        public beforeUnmount(): void {
            SickPeriodEditModal.instance = null;
        }

        public addDay(date: Date): Date {
            return Dates.Utils.addDays(date, 1);
        }

        public addPercentage(sickPeriod: SickPeriod): void {
            if (!sickPeriod.percentages) {
                sickPeriod.percentages = [];
            }
            const latestDate: Date = sickPeriod.percentages.length ? this.addDay(sickPeriod.percentages[sickPeriod.percentages.length - 1].beginDate) : this.date;
            sickPeriod.percentages.push({
                beginDate: latestDate,
                percentage: 100,
                creationDate: new Date(),
                sickPeriodId: sickPeriod.id,
            });
        }

        public getPercentageEarliestSelectableDate(sickPeriod: SickPeriod, index: number): Date {
            if (index > 0 && sickPeriod.percentages) {
                return this.addDay(sickPeriod.percentages[index - 1].beginDate);
            }

            return sickPeriod.beginDate;
        }

        public getPercentageLatestSelectableDate(sickPeriod: SickPeriod, index: number): Date | undefined {
            if (sickPeriod.percentages && sickPeriod.percentages[index + 1]) {
                return Dates.Utils.addDays(sickPeriod.percentages[index + 1].beginDate, -1);
            }

            return undefined;
        }

        public deletePercentage(sickPeriod: SickPeriod, index: number): void {
            if (sickPeriod.percentages) {
                sickPeriod.percentages.splice(index, 1);
            }
        }

        public saveSickPeriods(): void {
            if (this.isSaving) {
                return;
            }

            const promises: Promise<void | number>[] = [];

            for (const sickPeriod of this.sickPeriods) {
                for (let i: number = 1; i < sickPeriod.percentages!.length; i++) {
                    if (sickPeriod.percentages![i - 1].percentage === sickPeriod.percentages![i].percentage) {
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: 'Opeenvolgende ziekte percentages kunnen niet gelijk zijn.', type: 'warning'});

                        return;
                    }
                }

                if ('id' in sickPeriod) {
                    promises.push(ApiClient.instance.updateSickPeriod(sickPeriod).then((): void => {
                        this.$emit('update:sickPeriod', sickPeriod);
                    }));
                } else {
                    promises.push(ApiClient.instance.createSickPeriod(sickPeriod).then((id: number): void => {
                        sickPeriod.id = id;
                        this.$emit('create:sickPeriod', sickPeriod);
                    }));
                }
            }

            this.isSaving = true;

            Promise.all(promises)
                .then((): void => {
                    this.closeModal();
                })
                .catch((error: ApiError): void => {
                    this.handleErrors(error);
                })
                .finally((): void => {
                    this.isSaving = false;
                });
        }

        public handleErrors(error: ApiError): void {
            if (error.response) {
                if (error.response.rawResponse.status === HttpStatusCode.Conflict) {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'De nieuwe ziekteperiode overlapt met (een) andere ziekteperiode(s). Kies een andere begin- en/of einddatum en probeer het opnieuw.', type: 'warning', showDuration: 7500});

                    return;
                }

                if (error.response.data) {
                    for (const errorField in error.response.data.errors) {
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: error.response.data.errors[errorField].join('\n'), type: 'error'});
                    }

                    return;
                }
            }

            /** @see Notify.createNotification() */
            // @ts-expect-error TS2339
            Notify.__vfdConstructor.createNotification({text: 'Onbekende fout.', type: 'error'});
        }

        public closeModal(): void {
            this.show = false;
        }

        public static open(date: Date, sickPeriods: SickPeriod[]): void {
            this.instance!.date = date;
            this.instance!.sickPeriods = sickPeriods;
            this.instance!.show = true;
        }
    }
</script>

<style lang="scss" scoped>
    .percentageGrid {
        display: grid;
        grid-template-columns: 1fr 1fr minmax(3.25rem, min-content);
        align-items: center;
        gap: 1rem;

        .percentageDateInput {
            grid-column: 1;
        }
    }

    .percentageAddButton {
        margin-top: 1rem;
        text-align: right;
    }
</style>
